import { useFetch } from '../../../hooks/useFetch';
import {
  EmployeePayslip,
  EmployeePayslipMin,
} from '../../../models/employee-payslip';
import {
  BatchImportResult,
  PayslipPending,
} from '../../../@types/employee-payslip';
import { errorNotification } from '../../../providers/mantine-notifications';
import { translateServerHttpErrors } from '../../../utils/helpers';
import {
  UseCreateEmployeePayslipProps,
  UseFindEmployeePayslipByHashCodeProps,
  UseImportBatchPayslipProps,
  UseListEmployeePayslipByUserProps,
  UsePreviewBatchPayslipProps,
  UseSignatureEmployeePayslipProps,
} from '../../../utils/types/data/hooks/employee-payslip';
import {
  createEmployeePayslipRequest,
  deletePayslipRequest,
  findEmployeePayslipByHashCodeRequest,
  getPayslipPendingRequest,
  importBatchPayslipRequest,
  listEmployeePayslipByUserRequest,
  previewBatchPayslipRequest,
  sendVerificationCodeRequest,
  signatureEmployeePayslipRequest,
  verifyCpfRequest,
  verifyCodeRequest,
} from '../../employee-payslip';

export function useCreateEmployeePaySlip() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<EmployeePayslip>();

  const fetch = ({ data, onSuccess }: UseCreateEmployeePayslipProps) =>
    fetchData({
      fetcher: () => createEmployeePayslipRequest(data),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao enviar documento.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useListEmployeePayslipByUser() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<EmployeePayslip[]>();

  const fetch = ({
    employeeId,
    onSuccess,
  }: UseListEmployeePayslipByUserProps) =>
    fetchData({
      fetcher: () => listEmployeePayslipByUserRequest(employeeId),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar documentos.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useFindEmployeePayslipByHashCode() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<EmployeePayslipMin>();

  const fetch = ({
    hashCode,
    onSuccess,
    onError,
  }: UseFindEmployeePayslipByHashCodeProps) =>
    fetchData({
      fetcher: () => findEmployeePayslipByHashCodeRequest(hashCode),
      onSuccess,
      onError,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function usePreviewBatchPayslip() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<import('../../../@types/employee-payslip').BatchImportPreview>();

  const fetch = ({ file, onSuccess }: UsePreviewBatchPayslipProps) =>
    fetchData({
      fetcher: () => previewBatchPayslipRequest(file),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao ler o PDF.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return { fetch, response, error, loading };
}

export function useImportBatchPayslip() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<BatchImportResult>();

  const fetch = ({
    file,
    overrides,
    referenceMonth,
    mirrorPageIndexes,
    onSuccess,
  }: UseImportBatchPayslipProps) =>
    fetchData({
      fetcher: () =>
        importBatchPayslipRequest(
          file,
          overrides,
          referenceMonth,
          mirrorPageIndexes,
        ),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao importar holerites.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return { fetch, response, error, loading };
}

export function useVerifyCpf() {
  const {
    fetchData,
    state: { error, loading },
  } = useFetch<void>();

  const fetch = ({
    hashCode,
    cpf,
    onSuccess,
  }: {
    hashCode: string;
    cpf: string;
    onSuccess?: () => void;
  }) =>
    fetchData({
      fetcher: () => verifyCpfRequest(hashCode, cpf),
      onError: (error: any) => {
        errorNotification({
          title: 'CPF inválido.',
          message: translateServerHttpErrors(
            error,
            'Verifique e tente novamente.',
          ),
        });
      },
      onSuccess,
    });

  return { fetch, error, loading };
}

export function useSendVerificationCode() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<void>();

  const fetch = ({
    hashCode,
    onSuccess,
  }: {
    hashCode: string;
    onSuccess?: () => void;
  }) =>
    fetchData({
      fetcher: () => sendVerificationCodeRequest(hashCode),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao enviar código.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return { fetch, response, error, loading };
}

export function useVerifyCode() {
  const {
    fetchData,
    state: { error, loading },
  } = useFetch<void>();

  const fetch = ({
    hashCode,
    verificationCode,
    onSuccess,
  }: {
    hashCode: string;
    verificationCode: string;
    onSuccess?: () => void;
  }) =>
    fetchData({
      fetcher: () => verifyCodeRequest(hashCode, verificationCode),
      onError: (error: any) => {
        errorNotification({
          title: 'Código inválido.',
          message: translateServerHttpErrors(
            error,
            'Verifique e tente novamente.',
          ),
        });
      },
      onSuccess,
    });

  return { fetch, error, loading };
}

export function useSignatureEmployeePaySlip() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<void>();

  const fetch = ({ data, onSuccess }: UseSignatureEmployeePayslipProps) =>
    fetchData({
      fetcher: () => signatureEmployeePayslipRequest(data),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao assinar documento.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useDeletePayslip() {
  const {
    fetchData,
    state: { error, loading },
  } = useFetch<void>();

  const fetch = ({ id, onSuccess }: { id: number; onSuccess?: () => void }) =>
    fetchData({
      fetcher: () => deletePayslipRequest(id),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao remover holerite.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return { fetch, error, loading };
}

export function useGetPayslipPending() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<PayslipPending>();

  const fetch = ({
    onSuccess,
  }: { onSuccess?: (res: PayslipPending) => void } = {}) =>
    fetchData({
      fetcher: () => getPayslipPendingRequest(),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar pendências de holerites.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return { fetch, response, error, loading };
}
